import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Card, CardHeader, CardTitle, CardContent } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Input } from '@travellegacy/nexops-design-system';
import { CurrencyInput } from '@travellegacy/nexops-design-system';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
  DialogFooter,
} from '@travellegacy/nexops-design-system';
import { ConfirmDialog } from '@travellegacy/nexops-design-system';
import { Badge } from '@travellegacy/nexops-design-system';
import { TabFilter } from '@travellegacy/nexops-design-system';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@travellegacy/nexops-design-system';
import { customPaymentLinkAPI } from '@/shared/lib/api';
import { toast } from '@/shared/lib/toast';
import { formatCurrency, formatDate } from '@/shared/lib/utils';
import {
  Loader2,
  Link as LinkIcon,
  Plus,
  Copy,
  CheckCircle,
  Trash2,
  ToggleLeft,
  Package,
  RefreshCw,
  MessageSquare,
  Building2,
} from 'lucide-react';
import {
  PaymentType,
  LinkType,
  type CreateCustomPaymentLinkRequest,
  type CreateCustomPaymentLinkResponse,
  type CustomPaymentLink,
} from '@/shared/types';

// Schema de validação com mensagem amigável
const customLinkSchema = z.object({
  linkType: z.enum(['SERVICO', 'RENOVACAO', 'CONSULTORIA', 'AGENCIA']),
  amount: z
    .number({ invalid_type_error: 'Por favor, informe um valor válido' })
    .positive('Valor deve ser maior que zero'),
  paymentType: z.enum(['SINGLE', 'INSTALLMENTS']),
  installments: z.number().min(1).max(12).optional(),
  description: z.string().max(500).optional(),
  expiresAt: z.string().optional(),
  maxUses: z.number().min(1).optional(),
});

type CustomLinkFormData = z.infer<typeof customLinkSchema>;

const linkTypeOptions = [
  {
    value: LinkType.SERVICO,
    label: 'Serviço',
    description: 'Venda de novo serviço para cliente',
    icon: Package,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/20',
  },
  {
    value: LinkType.RENOVACAO,
    label: 'Renovação',
    description: 'Renovação de serviço existente',
    icon: RefreshCw,
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/20',
  },
  {
    value: LinkType.CONSULTORIA,
    label: 'Consultoria',
    description: 'Venda de consultoria',
    icon: MessageSquare,
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/20',
  },
  {
    value: LinkType.AGENCIA,
    label: 'Agência',
    description: 'Link de pagamento personalizado para agência',
    icon: Building2,
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/20',
  },
];

export default function CustomPaymentLinksPage() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentStep, setCurrentStep] = useState<'type' | 'details'>('type');
  const [linkToDeactivate, setLinkToDeactivate] = useState<CustomPaymentLink | null>(null);
  const [linkToDelete, setLinkToDelete] = useState<CustomPaymentLink | null>(null);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [amount, setAmount] = useState(0);
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<CustomLinkFormData>({
    resolver: zodResolver(customLinkSchema),
    defaultValues: {
      linkType: LinkType.SERVICO,
      paymentType: PaymentType.SINGLE,
      amount: 0,
      installments: 1,
      maxUses: 3,
    },
  });

  const selectedPaymentType = watch('paymentType');
  const selectedLinkType = watch('linkType');

  // Query para listar links
  const { data: linksData, isLoading } = useQuery({
    queryKey: ['custom-payment-links'],
    queryFn: async () => {
      const response = await customPaymentLinkAPI.list();
      return response.data.data as CustomPaymentLink[];
    },
  });

  // Calcular status de cada link e filtrar
  const linksWithStatus = useMemo(() => {
    if (!linksData) return [];

    return linksData.map((link) => {
      const isExpired = link.expiresAt && new Date(link.expiresAt) < new Date();
      const isLimitReached = link.maxUses !== null && link.timesUsed >= link.maxUses;
      const isActive = link.isActive && !isExpired && !isLimitReached;

      return { ...link, isActiveStatus: isActive };
    });
  }, [linksData]);

  // Filtrar links baseado na tab selecionada
  const filteredLinks = useMemo(() => {
    if (statusFilter === 'all') return linksWithStatus;
    if (statusFilter === 'active') return linksWithStatus.filter((link) => link.isActiveStatus);
    if (statusFilter === 'inactive') return linksWithStatus.filter((link) => !link.isActiveStatus);
    return linksWithStatus;
  }, [linksWithStatus, statusFilter]);

  // Calcular contagens para as tabs
  const statusCounts = useMemo(() => {
    return {
      all: linksWithStatus.length,
      active: linksWithStatus.filter((link) => link.isActiveStatus).length,
      inactive: linksWithStatus.filter((link) => !link.isActiveStatus).length,
    };
  }, [linksWithStatus]);

  // Mutation para criar link
  const createLinkMutation = useMutation({
    mutationFn: async (data: CreateCustomPaymentLinkRequest) => {
      const response = await customPaymentLinkAPI.create(data);
      return response.data.data as CreateCustomPaymentLinkResponse;
    },
    onSuccess: (data) => {
      toast.success('Link criado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['custom-payment-links'] });
      handleCloseCreateModal();
      // Copiar automaticamente
      copyToClipboard(data.link, data.token);
    },
    onError: () => {
      toast.error('Erro ao criar link. Tente novamente.');
    },
  });

  // Mutation para desativar link
  const deactivateMutation = useMutation({
    mutationFn: async (token: string) => {
      await customPaymentLinkAPI.deactivate(token);
    },
    onSuccess: () => {
      toast.success('Link desativado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['custom-payment-links'] });
      setLinkToDeactivate(null);
    },
    onError: () => {
      toast.error('Erro ao desativar link.');
      setLinkToDeactivate(null);
    },
  });

  // Mutation para deletar link
  const deleteMutation = useMutation({
    mutationFn: async (token: string) => {
      await customPaymentLinkAPI.delete(token);
    },
    onSuccess: () => {
      toast.success('Link deletado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['custom-payment-links'] });
      setLinkToDelete(null);
    },
    onError: () => {
      toast.error('Erro ao deletar link.');
      setLinkToDelete(null);
    },
  });

  const onSubmit = (data: CustomLinkFormData) => {
    const payload: CreateCustomPaymentLinkRequest = {
      amount: data.amount,
      paymentType: data.paymentType as PaymentType,
      linkType: data.linkType as LinkType,
      installments: data.paymentType === PaymentType.SINGLE ? 1 : data.installments,
      description: data.description,
      expiresAt: data.expiresAt || undefined,
      maxUses: data.maxUses,
    };

    createLinkMutation.mutate(payload);
  };

  const copyToClipboard = (link: string, token: string) => {
    navigator.clipboard.writeText(link);
    setCopiedToken(token);
    toast.success('Link copiado!');
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCurrentStep('type');
    setAmount(0);
    reset();
  };

  const handleSelectLinkType = (type: LinkType) => {
    setValue('linkType', type);
    setCurrentStep('details');
  };

  const handleAmountChange = (value: number) => {
    setAmount(value);
    setValue('amount', value);
  };

  const handleBackToTypeSelection = () => {
    setCurrentStep('type');
  };

  const handleDeactivate = () => {
    if (linkToDeactivate) {
      deactivateMutation.mutate(linkToDeactivate.token);
    }
  };

  const handleDelete = () => {
    if (linkToDelete) {
      deleteMutation.mutate(linkToDelete.token);
    }
  };

  const getLinkTypeLabel = (linkType: LinkType | string) => {
    const option = linkTypeOptions.find((opt) => opt.value === linkType);
    return option?.label || linkType;
  };

  const getLinkTypeColors = (linkType: LinkType | string) => {
    const option = linkTypeOptions.find((opt) => opt.value === linkType);
    return {
      bg: option?.bgColor || 'bg-primary/10',
      text: option?.color || 'text-primary',
      border: option?.borderColor || 'border-primary/20',
    };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Links de Pagamento</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Crie links personalizados para negociações e parcerias
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Link
        </Button>
      </div>

      {/* Create Modal - Multi-step */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl">
          <DialogClose onClick={handleCloseCreateModal} />
          <DialogHeader>
            <DialogTitle>
              {currentStep === 'type'
                ? 'Selecione o Tipo do Link'
                : 'Criar Novo Link de Pagamento'}
            </DialogTitle>
          </DialogHeader>

          {currentStep === 'type' ? (
            /* ETAPA 1: Seleção do Tipo */
            <div className="p-6">
              <div className="grid grid-cols-1 gap-4">
                {linkTypeOptions.map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleSelectLinkType(option.value)}
                      className={`flex items-start p-4 border-2 rounded-lg hover:shadow-md transition-all ${option.borderColor} ${option.bgColor} hover:scale-[1.02]`}
                    >
                      <div className={`p-3 rounded-lg ${option.bgColor} ${option.color}`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <div className="ml-4 text-left flex-1">
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          {option.label}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          {option.description}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            /* ETAPA 2: Detalhes do Link */
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="p-6 space-y-6">
                {/* Indicador do tipo selecionado */}
                <div
                  className={`flex items-center space-x-3 p-4 rounded-lg border-2 ${getLinkTypeColors(selectedLinkType).bg} ${getLinkTypeColors(selectedLinkType).border}`}
                >
                  <div className="flex items-center space-x-2 flex-1">
                    <span className={`text-sm font-medium ${getLinkTypeColors(selectedLinkType).text}`}>
                      Tipo selecionado:
                    </span>
                    <Badge
                      className={`${getLinkTypeColors(selectedLinkType).bg} ${getLinkTypeColors(selectedLinkType).text} ${getLinkTypeColors(selectedLinkType).border} font-semibold`}
                    >
                      {getLinkTypeLabel(selectedLinkType)}
                    </Badge>
                  </div>
                  <button
                    type="button"
                    onClick={handleBackToTypeSelection}
                    className={`text-sm font-medium ${getLinkTypeColors(selectedLinkType).text} hover:underline`}
                  >
                    Alterar
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <CurrencyInput
                    label="Valor"
                    value={amount}
                    onChange={handleAmountChange}
                    error={errors.amount?.message}
                    placeholder="0,00"
                    required
                  />

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Tipo de Pagamento
                    </label>
                    <select
                      {...register('paymentType')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    >
                      <option value="SINGLE">À Vista</option>
                      <option value="INSTALLMENTS">Parcelado</option>
                    </select>
                  </div>

                  {selectedPaymentType === PaymentType.INSTALLMENTS && (
                    <Input
                      label="Parcelas"
                      type="number"
                      min="1"
                      max="12"
                      placeholder="12"
                      error={errors.installments?.message}
                      {...register('installments', { valueAsNumber: true })}
                    />
                  )}

                  <Input
                    label="Máximo de Usos"
                    type="number"
                    min="1"
                    placeholder="3"
                    error={errors.maxUses?.message}
                    {...register('maxUses', { valueAsNumber: true })}
                  />

                  <Input
                    label="Data de Expiração (opcional)"
                    type="datetime-local"
                    error={errors.expiresAt?.message}
                    {...register('expiresAt')}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Descrição (opcional)
                  </label>
                  <textarea
                    {...register('description')}
                    placeholder="Ex: Parceria especial - 10% desconto"
                    className="w-full px-3 py-2 mt-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white resize-none"
                    rows={3}
                  />
                  {errors.description && (
                    <p className="text-sm text-red-500 mt-1">{errors.description.message}</p>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="secondary" onClick={handleBackToTypeSelection}>
                  Voltar
                </Button>
                <Button type="submit" disabled={createLinkMutation.isPending}>
                  {createLinkMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Criando...
                    </>
                  ) : (
                    'Criar Link'
                  )}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Deactivate Dialog */}
      <ConfirmDialog
        open={!!linkToDeactivate}
        onOpenChange={(open) => !open && setLinkToDeactivate(null)}
        title="Desativar Link"
        description={`Tem certeza que deseja desativar este link? Ele não poderá mais ser utilizado e nem ativado novamente.`}
        confirmText="Desativar"
        cancelText="Cancelar"
        onConfirm={handleDeactivate}
        variant="warning"
        icon="toggle"
        isLoading={deactivateMutation.isPending}
      />

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={!!linkToDelete}
        onOpenChange={(open) => !open && setLinkToDelete(null)}
        title="Deletar Link"
        description={`Tem certeza que deseja deletar este link? Esta ação não pode ser desfeita.`}
        confirmText="Deletar"
        cancelText="Cancelar"
        onConfirm={handleDelete}
        variant="destructive"
        icon="delete"
        isLoading={deleteMutation.isPending}
      />

      {/* Links Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <LinkIcon className="h-5 w-5" />
            <span>Links Criados</span>
          </CardTitle>

          {/* Status Tabs */}
          <TabFilter
            tabs={[
              {
                value: 'all',
                label: 'Todos',
                count: statusCounts.all,
                color: 'primary' as const,
              },
              {
                value: 'active',
                label: 'Ativos',
                count: statusCounts.active,
                color: 'green' as const,
              },
              {
                value: 'inactive',
                label: 'Inativos',
                count: statusCounts.inactive,
                color: 'red' as const,
              },
            ]}
            currentTab={statusFilter}
            onChange={(value) => setStatusFilter(value as 'all' | 'active' | 'inactive')}
          />
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : !linksData || linksData.length === 0 ? (
            <div className="text-center py-12">
              <LinkIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                Nenhum link customizado criado ainda
              </p>
            </div>
          ) : filteredLinks.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400">
                Nenhum link encontrado nesta categoria
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Valor</TableHead>
                  <TableHead>Pagamento</TableHead>
                  <TableHead>Usos</TableHead>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Expira em</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLinks.map((link) => {
                  const isExpired = link.expiresAt && new Date(link.expiresAt) < new Date();
                  const isLimitReached = link.maxUses !== null && link.timesUsed >= link.maxUses;
                  const fullLink = `${window.location.origin}/payment/custom/${link.token}`;

                  return (
                    <TableRow key={link.id} highlight={!link.isActiveStatus}>
                      <TableCell>
                        {link.isActiveStatus ? (
                          <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
                            Ativo
                          </Badge>
                        ) : (
                          <Badge className="bg-red-500/10 text-red-500 border-red-500/20">
                            {isExpired
                              ? 'Expirado'
                              : isLimitReached
                                ? 'Limite atingido'
                                : 'Inativo'}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={`${getLinkTypeColors(link.linkType).bg} ${getLinkTypeColors(link.linkType).text} ${getLinkTypeColors(link.linkType).border}`}
                        >
                          {getLinkTypeLabel(link.linkType)}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-semibold text-foreground">
                        {formatCurrency(link.amount)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {link.paymentType === PaymentType.SINGLE
                          ? 'À Vista'
                          : `${link.installments}x`}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {link.timesUsed} / {link.maxUses || '∞'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                        {link.description || '-'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {link.expiresAt ? formatDate(link.expiresAt) : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyToClipboard(fullLink, link.token)}
                            className="h-8 w-8 p-0"
                            title="Copiar link"
                          >
                            {copiedToken === link.token ? (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </Button>
                          {link.isActive && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setLinkToDeactivate(link)}
                              className="h-8 w-8 p-0 text-yellow-600 hover:text-yellow-700 dark:text-yellow-500 dark:hover:text-yellow-400"
                              title="Desativar link"
                            >
                              <ToggleLeft className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => setLinkToDelete(link)}
                            className="h-8 w-8 p-0"
                            title="Deletar link"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
